import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'

import {
    TripListApiResponse,
    TripListApiResponseWrapped,
    TripListBucket,
    TripListData,
    TripListItemRaw,
    TripListQueryParams,
    TripSummary
} from '@/src/types/trips'
import { isCleartrip } from '@/src/utils/generalUtils/isCleartrip'
import setCommonHeaders from '@/src/utils/rtkQuery/setCommonHeaders'

const baseQuery = fetchBaseQuery({
    baseUrl: '',
    credentials: 'include',
    prepareHeaders: headers => {
        const defaultHeaders = setCommonHeaders(headers)
        defaultHeaders.set('X-CT-SOURCETYPE', 'web')
        return defaultHeaders
    }
})

const baseQueryWithRetry = retry(baseQuery, { maxRetries: 0 })

const DEFAULT_PAYLOAD: Required<Pick<TripListQueryParams, 'pageNo' | 'pageSize' | 'tripStatus'>> = {
    pageNo: 0,
    pageSize: 10,
    tripStatus: 'upcoming'
}

const TRIP_LIST_API_PATH = process.env.NEXT_PUBLIC_TRIP_LIST_API_PATH as string

type TripBookingSource = 'MAIN' | 'LINKED'

const normalizeTrip = (trip: TripListItemRaw, bookingSource: TripBookingSource): TripSummary | null => {
    const tripId = trip.tripId ?? trip.trip_ref
    if (!tripId) return null

    const rawTripType = trip.tripType?.toString() || 'air'
    const tripType = rawTripType === 'H' ? 'H' : rawTripType.toLowerCase()

    return {
        tripId: String(tripId),
        tripRef: trip.trip_ref,
        tripName: trip.tripName || '--',
        tripType,
        travelDate: trip.travelDate || undefined,
        travellers: trip.travellers,
        bookingStatus: trip.bookingStatus,
        bookingDate: trip.bookingDate,
        sourceType: trip.sourceType,
        bookingSource
    }
}

const mapBucket = (bucket?: TripListBucket, bookingSource?: TripBookingSource): TripSummary[] => {
    if (!bucket?.tripListResponses?.length) return []
    const source = bookingSource ?? 'MAIN'
    return bucket.tripListResponses.map(t => normalizeTrip(t, source)).filter(Boolean) as TripSummary[]
}

const mergeBuckets = (
    mainBucket?: TripListBucket,
    linkedBucket?: TripListBucket
): { trips: TripSummary[]; total: number } => {
    const mainTrips = mapBucket(mainBucket, 'MAIN')
    const linkedTrips = mapBucket(linkedBucket, 'LINKED')
    return {
        trips: [...mainTrips, ...linkedTrips],
        total: (mainBucket?.totalElements ?? 0) + (linkedBucket?.totalElements ?? 0)
    }
}

/** Detect wrapped response (link-switch: ON) vs flat/legacy (link-switch: OFF) */
const isWrappedResponse = (r: unknown): r is TripListApiResponseWrapped =>
    typeof r === 'object' && r !== null && ('MAIN' in r || 'LINKED' in r)

const mapTripListResponse = (response?: TripListApiResponseWrapped | TripListApiResponse): TripListData => {
    const main: TripListApiResponse | undefined = isWrappedResponse(response)
        ? response.MAIN ?? undefined
        : (response as TripListApiResponse)
    const linked: TripListApiResponse | undefined = isWrappedResponse(response) ? response.LINKED ?? undefined : undefined

    const { trips: upcomingTrips, total: upcomingTotal } = mergeBuckets(main?.upcomingTrips, linked?.upcomingTrips)
    const { trips: expiredTrips, total: expiredTotal } = mergeBuckets(main?.expiredTrips, linked?.expiredTrips)
    const { trips: archivedTrips, total: archivedTotal } = mergeBuckets(main?.archivedTrips, linked?.archivedTrips)
    const { trips: completedTrips, total: completedTotal } = mergeBuckets(main?.completedTrips, linked?.completedTrips)

    const allTripsMap = new Map<string, TripSummary>()
    ;[upcomingTrips, expiredTrips, archivedTrips, completedTrips].forEach(bucket => {
        bucket.forEach(trip => {
            if (!allTripsMap.has(trip.tripId)) {
                allTripsMap.set(trip.tripId, trip)
            }
        })
    })

    return {
        upcomingTrips,
        expiredTrips,
        archivedTrips,
        completedTrips,
        allTrips: Array.from(allTripsMap.values()),
        upcomingTotal,
        expiredTotal,
        archivedTotal,
        completedTotal
    }
}

export const tripListApi = createApi({
    reducerPath: 'tripListApi',
    baseQuery: baseQueryWithRetry,
    tagTypes: ['TripList'],
    endpoints: builder => ({
        getTripList: builder.query<TripListData, TripListQueryParams | void>({
            query: body => {
                const cleartrip = isCleartrip()
                const requestBody = body || {}
                const fetchControl = requestBody.fetchControl ?? 'MAIN'
                const { fetchControl: _fetchControl, ...payloadBody } = requestBody
                return {
                    url: TRIP_LIST_API_PATH,
                    method: 'POST',
                    headers: cleartrip
                        ? {}
                        : { 'link-switch': 'ON', 'fetch-control': fetchControl },
                    body: {
                        ...DEFAULT_PAYLOAD,
                        ...payloadBody
                    }
                }
            },
            transformResponse: (response: TripListApiResponseWrapped | TripListApiResponse) => mapTripListResponse(response),
            providesTags: ['TripList']
        })
    })
})

export const { useGetTripListQuery } = tripListApi
export default tripListApi

